import {
  ConfirmationDialogBox,
  Grid,
  IconButton,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { BasicModal, BasicTable, Button } from '@components';
import { Column } from '@components/BasicTable/BasicTable';
import {
  checkIfDialogBoxIsOpen,
  closeDialogBox,
  openDialogBox,
} from '@store/dialogBox';
import { closeModal, openModal } from '@store/modals';
import { ICustomerPayment, updateLoadPropertyAtIndex } from '@store/orders';
import { DeleteIcon, InformationIcon } from '@atgfr8/icons';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { formatDate } from '@utils/dateFormat';
import { formatAmount, isAdmin, isSuperAdmin } from '@utils/utils';
import { AddMissingEfsCheckModal } from '@views/order/components';
import {
  CARRIER_INSTRUCTION_MODAL_ID,
  DELETE_CARRIER_PAYMENTS_LINE_ITEM_DIALOG_ID,
} from '@views/order/constants';
import { cloneDeep } from 'lodash';
import { Show, createSignal } from 'solid-js';

import { CheckDetails } from './CheckDetails';
import classes from './classes';

const iconStyles = {
  color: '#0070A2',
  cursor: 'pointer',
};
export const checkDetailsModalId = 'checkDetailsModal';
type Props = {
  load: ILoadEditViewModel;
  tabIndex: number;
};

export const CarrierPayments = (props: Props) => {
  const [deleteRow, setDeleteRow] = createSignal<number | null>(null);
  const [activeRow, setActiveRow] = createSignal<ICustomerPayment | null>(null);

  const modalId = `${CARRIER_INSTRUCTION_MODAL_ID}-${props.tabIndex}`;

  const columns: Column<ICustomerPayment>[] = [
    {
      name: 'Date',
      key: 'date',
      renderCell: (rowData: ICustomerPayment) => {
        if (!Boolean(rowData.paidDate)) {
          return '';
        }

        return formatDate({
          // @ts-expect-error `paidDate` is not null here.
          date: new Date(rowData.paidDate),
          formatString: 'MM/dd/yyyy',
        });
      },
    },
    {
      name: 'Description',
      key: 'description',
      renderCell: (rowData: ICustomerPayment) => rowData.description,
    },
    // @ts-expect-error - type sauce
    isAdmin() && {
      name: 'Reference',
      key: 'reference',
      renderCell: (rowData: ICustomerPayment) => rowData.referenceNum,
    },
    {
      name: 'Amount',
      key: 'amount',
      renderCell: (rowData: ICustomerPayment) =>
        formatAmount(rowData.amount ?? 0),
    },
    {
      name: 'Payment #',
      key: 'payment',
      renderCell: (rowData: ICustomerPayment) => rowData.checkNumber,
    },
    {
      name: 'Info',
      key: 'info',
      minWidth: 100,
      renderCell: (rowData: ICustomerPayment) => (
        <Grid container gap={1} alignItems="center">
          <Show
            when={
              rowData.checkNumber && (rowData.description || '').includes('EFS')
            }
            fallback={<div class="w-[40px]"></div>}
          >
            <IconButton
              onClick={() => {
                setActiveRow(rowData);
                openModal(checkDetailsModalId);
              }}
              dataTestId={`view-carrier-payment-info-button-${rowData.id}`}
            >
              <InformationIcon
                style={{ ...iconStyles, 'font-size': '1.5rem' }}
              />
            </IconButton>
          </Show>
          <IconButton
            disabled={!isSuperAdmin()}
            onClick={() => {
              openDialogBox(DELETE_CARRIER_PAYMENTS_LINE_ITEM_DIALOG_ID);
              setDeleteRow(rowData.id);
            }}
            class="ml-1!"
            dataTestId={`delete-carrier-payment-button-${rowData.id}`}
          >
            <DeleteIcon
              style={{ ...iconStyles, 'font-size': '1.5rem', color: '#B00020' }}
            />
          </IconButton>
        </Grid>
      ),
    },
  ];

  const footerRow = [
    {
      key: 'ideaTitle',
    },
    {
      key: '#',
    },
    {
      renderCell: () => (
        <Typography variant="body1" class={classes.totalLabel}>
          Outstanding
        </Typography>
      ),
      key: 'Outstanding',
    },
    {
      renderCell: () => (
        <Typography variant="body1" class={classes.totalAmount}>
          {formatAmount(
            (typeof props.load.lineItems !== 'undefined'
              ? props.load.lineItems
                  .filter((v) => v.operationType !== 'Delete')
                  .reduce(
                    (acc, item) =>
                      acc + Number(item.rate) * Number(item.quantity),
                    0,
                  )
              : 0) -
              (typeof props.load.payments !== 'undefined'
                ? props.load.payments
                    .filter((v) => v.operationType !== 'Delete')
                    .reduce((acc, item) => acc + Number(item.amount), 0)
                : 0),
          )}
        </Typography>
      ),
      key: '#',
    },
    {
      renderCell: () => (
        <Typography variant="body1" class={classes.totalLabel}>
          Total Paid
        </Typography>
      ),
      key: '#',
    },
    {
      key: '#',
      renderCell: () => (
        <Typography variant="body1" class={classes.totalAmount}>
          {typeof props.load.payments !== 'undefined'
            ? formatAmount(
                props.load.payments
                  .filter((v) => v.operationType !== 'Delete')
                  .reduce((acc, item) => acc + Number(item.amount), 0),
              )
            : 0}
        </Typography>
      ),
    },
  ];

  return (
    <>
      <div class="flex justify-end">
        <Show when={isAdmin()}>
          <Button
            label="Add EFS Payment"
            color="primary"
            onClick={() => openModal(modalId)}
            sx={{ color: '#FFF' }}
            dataTestId={formatDataTestId('Add EFS Payment', 'button')}
          />
        </Show>
      </div>
      <BasicTable<ICustomerPayment>
        columns={columns}
        rows={props.load.payments?.filter((v) => v.operationType !== 'Delete')}
        footerRow={footerRow}
        tableBodyClasses={classes.tableBg}
        tableFooterClasses={classes.tableFooter}
        tableBodyRowClasses={classes.tableRow}
        tableFooterRowClasses={classes.tableFooterRow}
      />
      <ConfirmationDialogBox
        closeDialogBox={(_reason) =>
          closeDialogBox(DELETE_CARRIER_PAYMENTS_LINE_ITEM_DIALOG_ID)
        }
        isDialogOpen={checkIfDialogBoxIsOpen(
          DELETE_CARRIER_PAYMENTS_LINE_ITEM_DIALOG_ID,
        )}
        dataTestId="deleteCarrierPaymentRow"
        id="deleteCarrierPaymentRow"
        title={'Are you sure you want to delete this payment?'}
        onSubmit={() => {
          const payments = cloneDeep([...(props.load.payments ?? [])]);
          const paymentIndex = payments.findIndex((x) => x.id === deleteRow());

          if (payments[paymentIndex].operationType == 'Insert') {
            payments.splice(paymentIndex, 1);
          } else {
            payments[paymentIndex] = {
              ...payments[paymentIndex],
              operationType: 'Delete',
            };
          }
          updateLoadPropertyAtIndex({ payments });
        }}
        onSubmitText="Delete"
      />
      <BasicModal
        title="Check Details"
        id={checkDetailsModalId}
        onClose={() => closeModal(checkDetailsModalId)}
        showClose={true}
        header={false}
        width={'40vw'}
        footer={false}
      >
        <CheckDetails activeRow={activeRow()!} />
      </BasicModal>
      <BasicModal
        title="Add Missing EFS Check"
        id={modalId}
        onClose={() => closeModal(modalId)}
        showClose={false}
        width={'70vw'}
        footer={false}
      >
        <AddMissingEfsCheckModal
          load={props.load}
          tabIndex={props.tabIndex}
          modalId={modalId}
        />
      </BasicModal>
    </>
  );
};
